import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import moment from 'moment/moment';
import { isNotNullOrUndefined } from 'Util/TypeGuards';

export interface IItineraryItemEntityAttributes extends IModelAttributes {
	dateTime: Date;
	title: string;
	description?: string;
	sortOrder: number;
	itineraryPlanId: string;
	itineraryPlan?: Models.ItineraryPlanEntity | Models.IItineraryPlanEntityAttributes;
}

@entity('ItineraryItemEntity', 'Itinerary Item')
export default class ItineraryItemEntity extends Model implements IItineraryItemEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The date and time when this itinerary item occurs.
	 */
	@observable
	@attribute<ItineraryItemEntity, Date>()
	public dateTime: Date;

	/**
	 * The title of the itinerary item.
	 */
	@observable
	@attribute<ItineraryItemEntity, string>()
	public title: string;

	/**
	 * A description of the itinerary item.
	 */
	@observable
	@attribute<ItineraryItemEntity, string>()
	public description?: string;

	/**
	 * The sort order for manual ordering.
	 */
	@observable
	@attribute<ItineraryItemEntity, number>()
	public sortOrder: number = 0;

	/**
	 * The ID of the itinerary plan this item belongs to.
	 */
	@observable
	@attribute()
	public itineraryPlanId: string;

	/**
	 * The itinerary plan this item belongs to.
	 */
	@observable
	@attribute({ isReference: true })
	public itineraryPlan?: Models.ItineraryPlanEntity | Models.IItineraryPlanEntityAttributes;

	constructor(attributes?: Partial<IItineraryItemEntityAttributes>) {
		super(attributes);
		makeObservable(this);
		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * @param attributes The raw attributes to assign to this model.
	 */
	@action
	public assignAttributes(attributes?: Partial<IItineraryItemEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (isNotNullOrUndefined(attributes.dateTime)) {
				if (attributes.dateTime === null) {
					this.dateTime = attributes.dateTime;
				} else {
					this.dateTime = moment(attributes.dateTime).toDate();
				}
			}
			if (attributes.title !== undefined) {
				this.title = attributes.title;
			}
			if (attributes.description !== undefined) {
				this.description = attributes.description;
			}
			if (attributes.sortOrder !== undefined) {
				this.sortOrder = attributes.sortOrder;
			}
			if (attributes.itineraryPlanId !== undefined) {
				this.itineraryPlanId = attributes.itineraryPlanId;
			}
			if (attributes.itineraryPlan !== undefined) {
				if (attributes.itineraryPlan === null) {
					this.itineraryPlan = attributes.itineraryPlan;
				} else if (attributes.itineraryPlan instanceof Models.ItineraryPlanEntity) {
					this.itineraryPlan = attributes.itineraryPlan;
					this.itineraryPlanId = attributes.itineraryPlan.id;
				} else {
					this.itineraryPlan = new Models.ItineraryPlanEntity(attributes.itineraryPlan);

					if (!this.itineraryPlan.id) {
						throw new Error('ItineraryPlanEntity must have an id when assigned to ItineraryItemEntity');
					}

					this.itineraryPlanId = this.itineraryPlan.id;
				}
			}
		}
	}
}
